import React, { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/Button';
import { Card, CardHeader, CardContent } from '../components/ui/Card';
import { PasswordInput } from '../components/ui/PasswordInput';
import { PasswordRequirements } from '../components/ui/PasswordRequirements';
import { validatePasswordStrength } from '../utils/passwordUtils';

const StyledPageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(
    135deg,
    ${({ theme }) => theme.colors.primary}20,
    ${({ theme }) => theme.colors.secondary}20
  );
  padding: ${({ theme }) => theme.spacing.lg};
`;

const StyledFormContainer = styled.div`
  width: 100%;
  max-width: 400px;
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};
`;

const StyledInput = styled.input<{ hasError?: boolean }>`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.md};
  border: 1px solid ${({ theme, hasError }) => (hasError ? theme.colors.danger : theme.colors.border)};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.fontSizes.base};
  background-color: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.textPrimary};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}20;
  }
`;

const StyledError = styled.div`
  color: ${({ theme }) => theme.colors.danger};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  text-align: center;
`;

const StyledTitle = styled.h1`
  text-align: center;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const StyledSubtitle = styled.p`
  text-align: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const StyledLink = styled(Link)`
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: none;
  text-align: center;
  display: block;
  margin-top: ${({ theme }) => theme.spacing.lg};

  &:hover {
    text-decoration: underline;
  }
`;

export const RegisterPage: React.FC = () => {
  const { t } = useTranslation();
  const { register, isAuthenticated, isLoading, error } = useAuth();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [showPasswordRequirements, setShowPasswordRequirements] = useState(false);

  if (isAuthenticated) {
    return <Navigate to='/onboarding' replace />;
  }

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      errors.firstName = t('auth.firstNameRequired');
    }
    if (!formData.lastName.trim()) {
      errors.lastName = t('auth.lastNameRequired');
    }
    if (!formData.email.trim()) {
      errors.email = t('auth.emailRequired');
    }
    if (!formData.password) {
      errors.password = t('auth.passwordRequired');
    } else {
      // Use client-side password validation
      const passwordValidation = validatePasswordStrength(formData.password);

      if (!passwordValidation.isValid) {
        errors.password = passwordValidation.errors[0]; // Show first error
      }
    }
    if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = t('auth.passwordsDoNotMatch');
    }

    setFormErrors(errors);

    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      // Note: Password is transmitted via HTTPS encryption
      // The password appears in browser dev tools network tab but is encrypted in transit
      // Server-side middleware sanitizes passwords from logs
      console.log('🔐 Registering user with HTTPS-encrypted password transmission');

      await register({
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        password: formData.password, // Plain text - encrypted by HTTPS, sanitized server-side
      });
    } catch (error) {
      console.error('Registration failed:', error);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error for this field when user starts typing
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }

    // Special handling for password confirmation
    if (name === 'confirmPassword' && formData.password && value !== formData.password) {
      setFormErrors((prev) => ({
        ...prev,
        confirmPassword: t('auth.passwordsDoNotMatch'),
      }));
    } else if (name === 'confirmPassword' && formData.password && value === formData.password) {
      setFormErrors((prev) => ({
        ...prev,
        confirmPassword: '',
      }));
    }
  };

  return (
    <StyledPageContainer>
      <StyledFormContainer>
        <Card padding='lg'>
          <CardHeader>
            <StyledTitle>{t('auth.register')}</StyledTitle>
            <StyledSubtitle>Start your financial journey with us</StyledSubtitle>
          </CardHeader>
          <CardContent>
            <StyledForm onSubmit={handleSubmit}>
              <StyledInput
                type='text'
                name='firstName'
                placeholder={t('auth.firstName')}
                value={formData.firstName}
                onChange={handleChange}
                hasError={!!formErrors.firstName}
                required
              />
              {formErrors.firstName && <StyledError>{formErrors.firstName}</StyledError>}

              <StyledInput
                type='text'
                name='lastName'
                placeholder={t('auth.lastName')}
                value={formData.lastName}
                onChange={handleChange}
                hasError={!!formErrors.lastName}
                required
              />
              {formErrors.lastName && <StyledError>{formErrors.lastName}</StyledError>}

              <StyledInput
                type='email'
                name='email'
                placeholder={t('auth.email')}
                value={formData.email}
                onChange={handleChange}
                hasError={!!formErrors.email}
                required
              />
              {formErrors.email && <StyledError>{formErrors.email}</StyledError>}

              <PasswordInput
                name='password'
                placeholder={t('auth.password')}
                value={formData.password}
                onChange={handleChange}
                hasError={!!formErrors.password}
                required
                onFocus={() => setShowPasswordRequirements(true)}
                onBlur={() => setShowPasswordRequirements(false)}
              />
              <PasswordRequirements
                password={formData.password}
                show={showPasswordRequirements || !!formData.password}
              />
              {formErrors.password && <StyledError>{formErrors.password}</StyledError>}

              <PasswordInput
                name='confirmPassword'
                placeholder={t('auth.confirmPassword')}
                value={formData.confirmPassword}
                onChange={handleChange}
                hasError={!!formErrors.confirmPassword}
                required
              />
              {formErrors.confirmPassword && <StyledError>{formErrors.confirmPassword}</StyledError>}

              {error && <StyledError>{error}</StyledError>}

              <Button type='submit' fullWidth isLoading={isLoading}>
                {t('auth.register')}
              </Button>
            </StyledForm>
            <StyledLink to='/login'>Already have an account? {t('auth.login')}</StyledLink>
          </CardContent>
        </Card>
      </StyledFormContainer>
    </StyledPageContainer>
  );
};
